import type { QuestTarget_ } from "../net/connection";

/** `g_strEffectName`, `direction_land.mse` (`PythonBackground.cpp:18`), as
 *  `tools/build_effects.py` names it in the manifest. */
export const QUEST_TARGET_EFFECT = "quest_target";

/** What the beacons need of the effect layer: `EffectRenderer`'s own spawn,
 *  follow and detach. */
export interface BeaconField {
  spawn(key: string, options: { xMm: number; yMm: number; zMm: number; followId: number }): unknown;
  follow(entityId: number, xMm: number, yMm: number, zMm: number): void;
  stopFollowing(entityId: number, key: string): void;
}

/** The anchor a target's beacon hangs on. Entity ids are never negative and
 *  -1 is the particle field's "follows nothing", so a target's anchor sits
 *  below both. */
export function beaconFollowId(targetId: number): number {
  return -2 - targetId;
}

/** The server's quest targets, each stood on the ground as the direction
 *  effect: `CPythonBackground::CreateTargetEffect`/`DeleteTargetEffect` over
 *  `CMapOutdoor::SpecialEffect_Create`/`SpecialEffect_Delete`
 *  (`PythonBackground.cpp:857-909`, `MapOutdoor.cpp:1388-1425`). */
export class QuestTargetBeacons {
  private readonly standing = new Set<number>();

  /** How many beacons stand. */
  get size(): number {
    return this.standing.size;
  }

  update(
    event: QuestTarget_,
    field: BeaconField,
    groundAtMm: (xMm: number, yMm: number) => number,
  ): void {
    const anchor = beaconFollowId(event.id);
    if (event.kind === "delete") {
      // `SpecialEffect_Delete` destroys the instance (`MapOutdoor.cpp:1415-1425`);
      // the id is freed with it, so a target made again stands a new beacon.
      if (!this.standing.delete(event.id)) return;
      field.stopFollowing(anchor, QUEST_TARGET_EFFECT);
      return;
    }
    const known = this.standing.has(event.id);
    if (event.kind === "update" && !known) return;
    // `CreateTargetEffect` stands it at the map's height under the point
    // (`PythonBackground.cpp:870-882`), and a second create for an id that has
    // one moves that instance rather than making another (`MapOutdoor.cpp:1392-1405`).
    const zMm = groundAtMm(event.xMm, event.yMm);
    field.follow(anchor, event.xMm, event.yMm, zMm);
    if (known) return;
    field.spawn(QUEST_TARGET_EFFECT, { xMm: event.xMm, yMm: event.yMm, zMm, followId: anchor });
    this.standing.add(event.id);
  }
}
